import * as React from "react";
import { Box, CircularProgress, Modal, Stack, Typography } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import ClearIcon from "@mui/icons-material/Clear";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PersonIcon from "@mui/icons-material/Person";
import moment from "moment";
import { getHeaders } from "../../../utils/HeaderHelper";

const HISTORY_PAGE_SIZE = 10;

type HistoryChange = {
  source_column_name: string;
  field_label?: string | null;
  old_value_text: string | null;
  new_value_text: string | null;
  old_value_display: string | null;
  new_value_display: string | null;
};

type HistoryEntry = {
  action_group_id: string;
  action_type: "create" | "update" | "delete" | "archive";
  source_record_id: number | null;
  changed_at: string;
  actor: string;
  snapshot?: Record<string, unknown> | null;
  changes: HistoryChange[];
};

interface Props {
  open: boolean;
  patientId: number | string | null;
  onClose: () => void;
  csrfToken: string;
}

const FIELD_ORDER: ReadonlyArray<string> = [
  "Medication Name",
  "Directions",
  "Dosage",
  "Route",
  "Instruction",
  "Indication",
  "Prescriber",
  "Source",
];

const COLUMN_TO_LABEL: Record<string, string> = {
  medication_product: "Medication Name",
  directions: "Directions",
  dosage: "Dosage",
  route: "Route",
  instructions: "Instruction",
  indication: "Indication",
  prescriber: "Prescriber",
  source: "Source",
};

const SNAPSHOT_KEY_BY_LABEL: Record<string, string> = {
  "Medication Name": "medication_product",
  Directions: "directions",
  Dosage: "dosage",
  Route: "route",
  Instruction: "instructions",
  Indication: "indication",
  Prescriber: "prescriber",
  Source: "source",
};

const headerForAction = (actionType: string) => {
  switch (actionType) {
    case "create":
      return "Created Medication";
    case "update":
      return "Updated Medication";
    case "delete":
    case "archive":
      return "Deleted Medication";
    default:
      return "Updated Medication";
  }
};

const toBlank = (value: unknown) => {
  if (value == null) return "(blank)";
  const str = `${value}`.trim();
  return str === "" ? "(blank)" : str;
};

const cardBorderForAction = (actionType: HistoryEntry["action_type"]) => {
  switch (actionType) {
    case "delete":
    case "archive":
      return "#FF0000";
    case "create":
      return "#07BC2A";
    case "update":
    default:
      return "#FFDC68";
  }
};

const rowLabelSx = {
  color: "#1E1E1E",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "18px",
};


const extractMedicationName = (
  snapshot: Record<string, unknown> | null | undefined,
): string => {
  if (!snapshot) return "";
  const candidates = [
    snapshot["medication_product"],
    snapshot["medication_name"],
    (snapshot as { medication?: unknown }).medication,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim() !== "") {
      return candidate.trim();
    }
  }
  return "";
};

const MedicationListHistoryModal: React.FC<Props> = ({
  open,
  patientId,
  onClose,
  csrfToken,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [entries, setEntries] = React.useState<HistoryEntry[]>([]);
  const [page, setPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [totalEntries, setTotalEntries] = React.useState<number>(0);


  React.useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, patientId]);

  React.useEffect(() => {
    if (!open || !patientId) {
      return;
    }

    setLoading(true);

    const query = new URLSearchParams({
      page: String(page),
      per_page: String(HISTORY_PAGE_SIZE),
    }).toString();
    fetch(`/patient_medications/${patientId}/history?${query}`, {
      method: "GET",
      headers: getHeaders(csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        const rawResource = result?.resource;
        const resource: HistoryEntry[] = Array.isArray(rawResource)
          ? rawResource
          : Array.isArray(rawResource?.resource)
            ? rawResource.resource
            : [];

        const pagination = rawResource?.pagination;
        if (pagination) {
          setTotalPages(Math.max(1, Number(pagination.total_pages) || 1));
          setTotalEntries(Number(pagination.total) || 0);
        } else {
          setTotalPages(1);
          setTotalEntries(resource.length);
        }

        const groupedByAction = new Map<string, HistoryEntry>();
        resource.forEach((entry) => {
          const existing = groupedByAction.get(entry.action_group_id);
          if (!existing) {
            groupedByAction.set(entry.action_group_id, {
              ...entry,
              changes: [...(entry.changes || [])],
            });
            return;
          }
          groupedByAction.set(entry.action_group_id, {
            ...existing,
            changes: [...existing.changes, ...(entry.changes || [])],
            snapshot: existing.snapshot || entry.snapshot,
          });
        });

        const normalized = Array.from(groupedByAction.values()).sort(
          (a, b) =>
            moment(b.changed_at).valueOf() - moment(a.changed_at).valueOf(),
        );
        setEntries(normalized);
      })
      .catch(() => {
        setEntries([]);
        setTotalPages(1);
        setTotalEntries(0);
      })
      .finally(() => setLoading(false));
  }, [open, patientId, csrfToken, page]);

  const buildCreateRows = (
    snapshot: Record<string, unknown> | null | undefined,
  ): [string, string, string][] => {
    if (!snapshot) return [];
    const rows: [string, string, string][] = [];
    FIELD_ORDER.forEach((label) => {
      const snapshotKey = SNAPSHOT_KEY_BY_LABEL[label];
      const rawValue = snapshot[snapshotKey];
      if (rawValue == null) return;
      const stringValue = `${rawValue}`.trim();
      if (stringValue === "") return;
      rows.push([label, "(blank)", stringValue]);
    });
    return rows;
  };

  const buildUpdateRows = (
    changes: HistoryChange[],
  ): [string, string, string][] => {
    const rows: [string, string, string][] = [];
    changes.forEach((change) => {
      const label =
        change.field_label || COLUMN_TO_LABEL[change.source_column_name];
      if (!label) return;
      const oldValue = toBlank(change.old_value_display ?? change.old_value_text);
      const newValue = toBlank(change.new_value_display ?? change.new_value_text);
      if (oldValue === newValue) return;
      rows.push([label, oldValue, newValue]);
    });
    return rows.sort(
      (a, b) => FIELD_ORDER.indexOf(a[0]) - FIELD_ORDER.indexOf(b[0]),
    );
  };

  const buildDeleteRows = (
    snapshot: Record<string, unknown> | null | undefined,
  ): [string, string, string][] => {
    const medicationName = extractMedicationName(snapshot);
    const copy = medicationName
      ? `${medicationName} was removed`
      : "Medication was removed";
    return [[copy, "", ""]];
  };

  const buildRowsForEntry = (entry: HistoryEntry): [string, string, string][] => {
    if (entry.action_type === "create") return buildCreateRows(entry.snapshot);
    if (entry.action_type === "update") return buildUpdateRows(entry.changes || []);
    if (entry.action_type === "delete" || entry.action_type === "archive") {
      return buildDeleteRows(entry.snapshot);
    }
    return [];
  };

  return (
    <Modal
      open={open}
      className="medications-history-modal"
      onClose={onClose}
    >
      <div
        className="paper"
        style={{
          maxWidth: "920px",
          height: "88vh",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div className="paperInner">
          <Box>
            <Box className="main-header-container">
              <div
                className="main-header"
                style={{
                  marginBottom: "0px",
                  borderBottom: "1px solid #E7E7E7",
                }}
              >
                <span>Medication List History</span>
                <button
                  id="dismiss-button"
                  onClick={onClose}
                  type="button"
                  style={{
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                  }}
                  aria-label="Close medication history"
                >
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </button>
              </div>
            </Box>
            <div className="modal-content">
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!loading && entries.length === 0 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    color: "#666",
                  }}
                >
                  No medication history available
                </Typography>
              )}
              {!loading && entries.length > 0 && (
                <Stack
                  spacing={1}
                  sx={{ maxHeight: "68vh", overflowY: "auto" }}
                >
                  {entries.map((entry) => {
                    const rows = buildRowsForEntry(entry);
                    const isDelete =
                      entry.action_type === "delete" ||
                      entry.action_type === "archive";

                    return (
                      <Box
                        key={entry.action_group_id}
                        className="tile-container"
                      >
                        <Box
                          sx={{
                            width: "2px",
                            borderLeft: "2px solid",
                            borderColor: cardBorderForAction(entry.action_type),
                          }}
                        />
                        <Box>
                          <Typography className="tile-heading">
                            {headerForAction(entry.action_type)}
                          </Typography>
                          <Stack direction="row" spacing={1.5} sx={{ mb: 1.25 }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ color: "#8D8D8D" }}
                            >
                              <EventNoteIcon sx={{ fontSize: 14 }} />
                              <Typography
                                sx={{
                                  fontFamily: "QuicksandMedium",
                                  color: "#8D8D8D",
                                  fontSize: "12px",
                                }}
                              >
                                {moment(entry.changed_at).format(
                                  "MMMM D, YYYY",
                                )}{" "}
                                • {moment(entry.changed_at).format("h:mmA")}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ color: "#8D8D8D" }}
                            >
                              <PersonIcon sx={{ fontSize: 14 }} />
                              <Typography
                                sx={{
                                  fontFamily: "QuicksandMedium",
                                  color: "#8D8D8D",
                                  fontSize: "12px",
                                }}
                              >
                                {entry.actor}
                              </Typography>
                            </Stack>
                          </Stack>

                          {isDelete ? (
                            <Typography sx={rowLabelSx}>
                              {rows[0]?.[0]}
                            </Typography>
                          ) : (
                            <Stack
                              component="ul"
                              sx={{ margin: 0, paddingLeft: "20px" }}
                              spacing={0.3}
                            >
                              {rows.map((row, index) => (
                                <Box
                                  component="li"
                                  key={`${entry.action_group_id}-${index}`}
                                >
                                  <Typography sx={rowLabelSx}>
                                    <Box component="span" sx={rowLabelSx}>
                                      {row[0]}:
                                    </Box>{" "}
                                    {row[1]} {"→"} {row[2]}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
              {!loading && totalPages > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: "16px",
                    marginBottom: "10px",
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_event: React.ChangeEvent<unknown>, nextPage: number) =>
                      setPage(nextPage)
                    }
                    boundaryCount={3}
                  />
                </Box>
              )}
            </div>
          </Box>
        </div>
      </div>
    </Modal>
  );
};

export default MedicationListHistoryModal;
